import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-light-svg-icons/faCircleNotch';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle';
import { faHourglassHalf } from '@fortawesome/pro-light-svg-icons/faHourglassHalf';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { PaymentCleanCopies } from '../watermarkUpsell/cleanCopies/PaymentCleanCopies';
import { PaymentWatermarkSummary } from '../watermarkUpsell/paymentWatermarkSummary';
import { withBrandWord } from '../components/primitives/BrandWord';
import { PaymentPageStatus } from './checkoutSessionState';

interface PaymentStateViewProps {
  status: PaymentPageStatus;
  summaryBefore: PaymentWatermarkSummary | null;
  onContinue: () => void;
  onOpenPlans: () => void;
}

/** The text of each state of the return from a plan checkout. */
const PaymentStateViewComponent: React.FC<PaymentStateViewProps> = ({
  status,
  summaryBefore,
  onContinue,
  onOpenPlans,
}) => {
  const intl = useIntl();
  const continueLabel = intl.formatMessage({
    id: 'app.sml.payment.complete.success.button',
    defaultMessage: 'Continue to Dashboard',
  });

  switch (status) {
    case 'checking':
      return (
        <div className="text-center">
          <FontAwesomeIcon icon={faCircleNotch} size="3x" spin className="mb-4 text-primary" />
          <h2 className="mb-3">
            {intl.formatMessage({
              id: 'app.sml.payment.complete.checking.title',
              defaultMessage: 'Verifying your subscription...',
            })}
          </h2>
          <p className="text-muted">
            {intl.formatMessage({
              id: 'app.sml.payment.complete.checking.description',
              defaultMessage: 'Please wait while we confirm your payment.',
            })}
          </p>
        </div>
      );

    case 'success':
      // design: trial-upsell b5. The clean copies show when the first paid plan started a run.
      return (
        <PaymentCleanCopies before={summaryBefore}>
          <div className="text-center">
            <FontAwesomeIcon icon={faCheckCircle} size="3x" className="mb-4 text-success" />
            <h2 className="mb-3">
              {withBrandWord(
                intl.formatMessage({
                  id: 'app.sml.payment.complete.success.trial.title',
                  defaultMessage: 'Welcome to MUSIC24!',
                })
              )}
            </h2>
            <p className="text-muted mb-4">
              {intl.formatMessage({
                id: 'app.sml.payment.complete.success.trial.description',
                defaultMessage: 'Your subscription is active. Explore the dashboard to get started.',
              })}
            </p>
            <SMLButton onClick={onContinue}>{continueLabel}</SMLButton>
          </div>
        </PaymentCleanCopies>
      );

    case 'pending':
      return (
        <div className="text-center">
          <FontAwesomeIcon icon={faHourglassHalf} size="3x" className="mb-4 text-warning" />
          <h2 className="mb-3">
            {intl.formatMessage({
              id: 'app.sml.payment.complete.pending.title',
              defaultMessage: 'Payment processing',
            })}
          </h2>
          <p className="text-muted mb-4">
            {intl.formatMessage({
              id: 'app.sml.payment.complete.pending.description',
              defaultMessage:
                'Your payment is being processed. This may take a few minutes. You can continue using the app and your subscription will be activated shortly.',
            })}
          </p>
          <SMLButton onClick={onContinue}>
            {intl.formatMessage({
              id: 'app.sml.payment.complete.pending.button',
              defaultMessage: 'Continue to Dashboard',
            })}
          </SMLButton>
        </div>
      );

    case 'failed':
    case 'noSession':
    default:
      return (
        <div className="text-center" data-testid={`payment-${status}`}>
          <FontAwesomeIcon icon={faCircleExclamation} size="3x" className="mb-4 text-muted" />
          <h2 className="mb-3">
            {status === 'noSession'
              ? intl.formatMessage({
                  id: 'app.sml.payment.complete.noSession.title',
                  defaultMessage: 'No payment to confirm',
                })
              : intl.formatMessage({
                  id: 'app.sml.payment.complete.failed.title',
                  defaultMessage: 'We could not confirm the payment',
                })}
          </h2>
          <p className="text-muted mb-4">
            {status === 'noSession'
              ? intl.formatMessage({
                  id: 'app.sml.payment.complete.noSession.description',
                  defaultMessage: 'This page opens after a checkout. This link has no checkout in it.',
                })
              : intl.formatMessage({
                  id: 'app.sml.payment.complete.failed.description',
                  defaultMessage:
                    'We found no paid checkout for this link. If you paid, your plan shows in Settings → Subscription in a few minutes. If not, choose a plan again.',
                })}
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <SMLButton onClick={onOpenPlans}>
              {intl.formatMessage({ id: 'app.sml.payment.complete.failed.plans', defaultMessage: 'See plans' })}
            </SMLButton>
            <SMLButton outline onClick={onContinue}>
              {continueLabel}
            </SMLButton>
          </div>
        </div>
      );
  }
};

export const PaymentStateView = TypedMemo(PaymentStateViewComponent);
